import React, { useEffect, useState, useCallback } from 'react';
import { Table, Icon, Segment, Container, List, Button, Grid, Label } from 'semantic-ui-react';
import { getURLParameters, paramsToSearchURL, truncateString, orderBy } from '../../../../utils';
import './styles.scss';
import { DateTime } from 'luxon';
import { fetch } from 'services/http';
import { numFormat, numParse } from '@rededompedro/utils';
import FilterModal from './FilterModal';
import history from 'services/history';
import AttachmentsModal from './AttachmentsModal';

export function Details(props) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [attachment, setAttachment] = useState(null);

    const [column, setColumn] = useState(null);
    const [direction, setDirection] = useState(null);

    const [currentFilters, setCurrentFilters] = useState([]);
    const [filtering, setFiltering] = useState('all');

    const columns = [
        { name: 'numero', text: 'Número' },
        { name: 'emissao', text: 'Emissão' },
        { name: 'cliente', text: 'Cliente' },
        { name: 'forma_pgto', text: 'Forma Pgto.' },
        { name: 'valor', text: 'Valor' },
        { name: 'billed', text: 'Faturado', otherProps: { textAlign: 'center' } },
        { name: 'autoSystemLinked', text: 'Control Docs', otherProps: { textAlign: 'center' } },
    ];

    const handleSort = useCallback(
        (clickedColumn) => {
            return () => {
                if (column !== clickedColumn) {
                    setColumn(clickedColumn);
                    const ordering = orderBy(data, [clickedColumn], ['asc'], (a, b, prop) => {
                        if (prop === 'emissao') {
                            const dt1 = DateTime.fromFormat(String(a), 'yyyyMMdd');
                            const dt2 = DateTime.fromFormat(String(b), 'yyyyMMdd');
                            return dt1.toMillis() - dt2.toMillis();
                        }
                        if (!isNaN(a) && !isNaN(b)) {
                            return a - b;
                        }
                        return a > b ? 1 : a < b ? -1 : 0;
                    });
                    setData(ordering);
                    setDirection('ascending');
                } else {
                    setData(data.reverse());
                    setDirection(direction === 'ascending' ? 'descending' : 'ascending');
                }
            };
        },
        [column, data, direction],
    );

    const loadCurrentFilters = useCallback(async () => {
        const params = getURLParameters(props.location.search);
        const filters = [];
        for (const p in params) {
            switch (p) {
                case 'unidade': {
                    const data = await (
                        await fetch(`/common/company-by-id?id=${params[p]}`)
                    ).json();
                    filters.push({ name: 'unidade', value: data.nome_red });
                    break;
                }

                case 'data_cupom':
                    const dataCupom = params[p]
                        .split(',')
                        .map((it) => DateTime.fromISO(it).toFormat('dd/MM/yyyy'));
                    filters.push({ name: 'perído', value: `${dataCupom[0]} - ${dataCupom[1]}` });
                    break;

                case 'data_fatura':
                    const dataFatura = params[p]
                        .split(',')
                        .map((it) => DateTime.fromISO(it).toFormat('dd/MM/yyyy'));
                    filters.push({ name: 'perído', value: `${dataFatura[0]} - ${dataFatura[1]}` });
                    break;

                case 'forma_pgto':
                    const data = await (
                        await fetch(`/common/payment-type-by-id?id=${params[p]}`)
                    ).json();
                    filters.push({
                        name: 'forma(s) pgto.',
                        value: data.map((it) => it.nome).join(', '),
                    });
                    break;

                case 'numero_cupom':
                    filters.push({ name: 'número cupom', value: params[p] });
                    break;

                case 'numero_fatura':
                    filters.push({ name: 'número fatura', value: params[p] });
                    break;

                case 'valor_cupom':
                    filters.push({ name: 'valor cupom', value: numFormat(params[p]) });
                    break;

                case 'valor_fatura':
                    filters.push({ name: 'valor fatura', value: numFormat(params[p]) });
                    break;

                default:
            }
        }
        setCurrentFilters(filters);
    }, [props.location.search]);

    useEffect(() => {
        const params = getURLParameters(props.location.search);
        loadCurrentFilters();

        fetch(`/control-docs/receipts?${paramsToSearchURL(params)}`)
            .then((response) => response.json())
            .then((data) => {
                setData(data);
                setFilterModalOpen(false);
            })
            .catch((e) => console.log(e))
            .finally(() => setLoading(false));
    }, [loadCurrentFilters, props.location.search]);

    const onFilter = useCallback((data) => {
        const params = {};
        for (let param in data) {
            if (
                !data[param] ||
                (Array.isArray(data[param]) && !data[param].filter((it) => it).length)
            ) {
                continue;
            }

            switch (param) {
                case 'data_cupom':
                case 'data_fatura':
                    params[param] = data[param].map((it) => DateTime.fromJSDate(it).toISODate());
                    break;

                case 'valor_cupom':
                case 'valor_fatura':
                    params[param] = numParse(data[param]);
                    break;

                default:
                    params[param] = data[param];
            }
        }
        history.push({ search: paramsToSearchURL(params) });
    }, []);

    function handleReceiptNumberClick(id) {
        setAttachment(id);
    }

    // const handleCurrentFilter = (status) => {
    //     return () => {
    //         switch (status) {
    //             case 'all':
    //                 setCurrentFilter(() => true);
    //                 break;

    //             case 'success':
    //                 setCurrentFilter((it) => it.autoSystemLinked);
    //                 break;

    //             case 'failed':
    //                 setCurrentFilter((it) => !it.autoSystemLinked);
    //                 break;

    //             default:
    //         }
    //     }
    // }

    // console.log('data', data);

    return (
        <Container fluid id="conference-coupons-details" style={{ paddingTop: 15 }}>
            <FilterModal
                open={filterModalOpen}
                onClose={() => setFilterModalOpen(false)}
                onFilter={onFilter}
            />
            <AttachmentsModal attachmentId={attachment} onClose={() => setAttachment(null)} />
            <Segment>
                <Grid>
                    <Grid.Row columns={2} style={{ margin: 0 }}>
                        <Grid.Column>
                            <List>
                                <List.Item>
                                    <List.Icon name="ordered list" />
                                    <List.Content>Número de cupons: {data?.length}</List.Content>
                                </List.Item>
                                <List.Item>
                                    <List.Icon name="linkify" />
                                    <List.Content>
                                        Vinculados:{' '}
                                        {data?.filter((it) => it.autoSystemLinked)?.length} /{' '}
                                        {data?.length}
                                    </List.Content>
                                </List.Item>
                                <List.Item>
                                    <List.Icon name="linkify" />
                                    <List.Content>
                                        Faturados: {data?.filter((it) => it.billed)?.length} /{' '}
                                        {data?.length}
                                    </List.Content>
                                </List.Item>
                                <List.Item>
                                    <List.Content>
                                        <div id="current-filters">
                                            {currentFilters.map((it) => (
                                                <Label key={it.name}>
                                                    {it.name}
                                                    <Label.Detail>{it.value}</Label.Detail>
                                                </Label>
                                            ))}
                                        </div>
                                    </List.Content>
                                </List.Item>
                            </List>
                        </Grid.Column>
                        <Grid.Column textAlign="right">
                            <Button.Group size="mini" style={{ marginRight: 10 }}>
                                <Button onClick={() => setFiltering('all')}>
                                    <Icon name="list" />
                                    Todos
                                </Button>
                                <Button onClick={() => setFiltering('failed')}>
                                    <Icon name="remove" />
                                    C. Docs
                                </Button>
                                <Button onClick={() => setFiltering('success')}>
                                    <Icon name="check" />
                                    C. Docs
                                </Button>
                            </Button.Group>
                            <Button type="button" primary onClick={() => setFilterModalOpen(true)}>
                                <Icon name="filter" /> Filtrar
                            </Button>
                            <Button type="button" color="red" onClick={() => history.goBack()}>
                                <Icon name="arrow left" /> Voltar
                            </Button>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Segment>
            <Table selectable sortable celled>
                <Table.Header>
                    <Table.Row>
                        {columns.map((it) => (
                            <Table.HeaderCell
                                key={it.name}
                                sorted={column === it.name ? direction : null}
                                onClick={handleSort(it.name)}
                            >
                                {it.text}
                            </Table.HeaderCell>
                        ))}
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {(() => {
                        if (loading && !data) {
                            return (
                                <Table.Row>
                                    <Table.Cell colSpan={3}>Carregando...</Table.Cell>
                                </Table.Row>
                            );
                        }

                        if (!loading && data.length === 0) {
                            return (
                                <Table.Row>
                                    <Table.Cell colSpan={3}>Não há dados para exibir.</Table.Cell>
                                </Table.Row>
                            );
                        }

                        return data
                            .filter((it) =>
                                filtering === 'all'
                                    ? true
                                    : filtering === 'success'
                                    ? it.autoSystemLinked
                                    : !it.autoSystemLinked,
                            )
                            .map((it, i) => (
                                <Table.Row key={`details-${i}`}>
                                    <Table.Cell
                                        className={it.detailable ? 'detailable-receipt' : null}
                                        onClick={
                                            it.detailable
                                                ? () => handleReceiptNumberClick(it.id)
                                                : () => {}
                                        }
                                    >
                                        {it.numero}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {DateTime.fromFormat(
                                            String(it.horario),
                                            'yyyyMMddHHmmss',
                                        ).toFormat('dd/MM/yyyy HH:mm:ss')}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {it.cliente_cod} - {truncateString(it.cliente, 30)}
                                    </Table.Cell>
                                    <Table.Cell>{it.forma_pgto}</Table.Cell>
                                    <Table.Cell>R$ {numFormat(it.valor)}</Table.Cell>
                                    <Table.Cell textAlign="center">
                                        {it.billed ? (
                                            <Icon name="check circle" color="green" />
                                        ) : (
                                            <Icon name="remove" color="yellow" />
                                        )}
                                    </Table.Cell>
                                    <Table.Cell textAlign="center">
                                        {it.autoSystemLinked ? (
                                            <Icon name="check circle" color="green" />
                                        ) : (
                                            <Icon name="remove" color="red" />
                                        )}
                                    </Table.Cell>
                                </Table.Row>
                            ));
                    })()}
                </Table.Body>
            </Table>
        </Container>
    );
}
